import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
  ChevronLeft, Users, Calendar, TrendingUp, Award, 
  BadgeCheck, MapPin, Clock, CheckCircle, Send, FileText,
  Phone, Mail, Briefcase, GraduationCap, Target, Loader2,
  AlertCircle, HourglassIcon, Linkedin, Globe, AtSign,
  Shield, Euro, Calculator, XCircle, ArrowRight, Zap,
  Lock, PiggyBank, BarChart3, Percent, Camera, Upload,
  Crown, Rocket, Star, Gift, Timer, ChevronDown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import LanguageSelector from '../components/LanguageSelector';
import SimpleCoachForm from '../components/SimpleCoachForm';
import JourneySimulator from '../components/JourneySimulator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand colors
const COLORS = {
  darkBlue: '#1a2d56',
  green: '#d7de3b',
  greenDark: '#b8bf2f',
};

const specialties = [
  'Yoga', 'Pilates', 'Strength Training', 'Cardio', 'HIIT',
  'Boxing', 'Dance', 'Stretching', 'Running', 'Outdoor fitness',
  'CrossFit', 'Swimming', 'Cycling', 'Martial arts', 'Other'
];

// Default launch stats (fallback if API fails)
const DEFAULT_STATS = {
  totalSpots: 90,
  currentCoaches: 11,
  closingDate: '2026-04-15',
};

// Pricing Plans Data
// Pricing Plans - will be translated via t() function in components
const basePricingPlans = [
  {
    id: 'freemium',
    name: 'Freemium',
    price: 0,
    commission: 30,
    color: 'gray',
    featureKeys: ['publishSessions', 'manageBookings', 'securePaymentFeature', 'basicStats'],
    excludedKeys: ['prioritySupport', 'searchVisibility', 'premiumBadge'],
    ctaKey: 'startFree',
    popular: false
  },
  {
    id: 'active',
    name: 'Active',
    price: 39,
    commission: 20,
    color: 'primary',
    featureKeys: ['publishSessions', 'manageBookings', 'securePaymentFeature', 'basicStats', 'prioritySupport', 'searchVisibility'],
    excludedKeys: ['premiumBadge'],
    ctaKey: 'chooseActive',
    popular: true
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 79,
    commission: 10,
    color: 'yellow',
    featureKeys: ['publishSessions', 'manageBookings', 'securePaymentFeature', 'advancedAnalytics', 'prioritySupport', 'searchVisibility', 'premiumBadge'],
    excludedKeys: [],
    ctaKey: 'choosePremium',
    popular: false
  }
];

// Helper function to get translated pricing plans
const getPricingPlans = (t) => basePricingPlans.map(plan => ({
  ...plan,
  features: [
    ...plan.featureKeys.map(key => ({ text: t(`coach.${key}`), included: true })),
    ...plan.excludedKeys.map(key => ({ text: t(`coach.${key}`), included: false }))
  ],
  cta: t(`coach.${plan.ctaKey}`)
}));

// ==================== COMPONENTS ====================

// Progress Counter Component
function LaunchCounter({ current, total, t }) {
  const percentage = Math.round((current / total) * 100);
  
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-white/70">{t('coach.reservedSpots')}</span>
        <span className="font-bold" style={{ color: COLORS.green }}>{current} / {total}</span>
      </div>
      <div className="h-3 bg-white/10 rounded-full overflow-hidden">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${COLORS.green}, ${COLORS.greenDark})` }}
        />
      </div>
      <p className="text-center mt-2 text-sm text-white/60">
        {t('coach.alreadyJoined').replace('{count}', current)}
      </p>
    </div>
  );
}

// Sticky CTA Component
function StickyCTA({ onClick, visible, t }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-black via-black/95 to-transparent"
        >
          <div className="max-w-lg mx-auto">
            <Button 
              onClick={onClick}
              className="w-full py-6 text-lg font-bold rounded-xl shadow-2xl"
              style={{ background: COLORS.green, color: COLORS.darkBlue }}
              data-testid="sticky-cta"
            >
              <Rocket className="w-5 h-5 mr-2" />
              {t('coach.becomeAmbassador')}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Revenue Simulator Component - UNLOCKED VERSION
function RevenueSimulator({ onSelectPlan, t }) {
  const [pricePerPerson, setPricePerPerson] = useState(12);
  const [minParticipants, setMinParticipants] = useState(6);
  const [sessionsPerMonth, setSessionsPerMonth] = useState(16);
  const [selectedPlan, setSelectedPlan] = useState('auto'); // 'auto' for automatic recommendation

  const pricingPlans = getPricingPlans(t);
  const grossRevenue = pricePerPerson * minParticipants * sessionsPerMonth;
  const paymentFeesRate = 0.015; // 1.5% payment fees
  const paymentFeesAmount = grossRevenue * paymentFeesRate;

  // Calculate net revenue for each plan as Ambassador
  const calculateAmbassadorRevenue = (planId) => {
    const plan = pricingPlans.find(p => p.id === planId);
    if (!plan) return 0;
    const ambassadorCommissionRate = (plan.commission / 100) / 2;
    const commission = grossRevenue * ambassadorCommissionRate;
    return Math.round(grossRevenue - commission - paymentFeesAmount - plan.price);
  };

  // Find the best plan automatically
  const freemiumRevenue = calculateAmbassadorRevenue('freemium');
  const activeRevenue = calculateAmbassadorRevenue('active');
  const premiumRevenue = calculateAmbassadorRevenue('premium');

  // Determine the best plan (highest revenue)
  const getBestPlan = () => {
    // Freemium doesn't have Ambassador, so compare its standard revenue
    const freemiumStandard = Math.round(grossRevenue - (grossRevenue * 0.30) - paymentFeesAmount - 0);
    
    if (premiumRevenue >= activeRevenue && premiumRevenue >= freemiumStandard) return 'premium';
    if (activeRevenue >= premiumRevenue && activeRevenue >= freemiumStandard) return 'active';
    return 'freemium';
  };

  const bestPlan = getBestPlan();
  const effectivePlan = selectedPlan === 'auto' ? bestPlan : selectedPlan;
  
  const plan = pricingPlans.find(p => p.id === effectivePlan) || pricingPlans[1];
  const commissionRate = plan.commission / 100;
  const ambassadorCommissionRate = commissionRate / 2; // Ambassador gets 50% off
  const monthlyFee = plan.price;
  
  // Check if current plan has Ambassador benefits (not Freemium)
  const hasAmbassadorBenefits = effectivePlan !== 'freemium';

  // Calculations - Standard
  const commissionAmount = grossRevenue * commissionRate;
  const netRevenue = Math.round(grossRevenue - commissionAmount - paymentFeesAmount - monthlyFee);
  const yearlyRevenue = netRevenue * 12;

  // Calculations - Ambassador (only for non-Freemium)
  const ambassadorCommissionAmount = grossRevenue * ambassadorCommissionRate;
  const ambassadorNetRevenue = Math.round(grossRevenue - ambassadorCommissionAmount - paymentFeesAmount - monthlyFee);
  const ambassadorYearlyRevenue = ambassadorNetRevenue * 12;
  const monthlyBonus = ambassadorNetRevenue - netRevenue;

  return (
    <div className="bg-gradient-to-br from-[#1a2d56] to-[#0f1a33] border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${COLORS.green}20` }}>
          <Calculator className="w-6 h-6" style={{ color: COLORS.green }} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{t('coach.simulateRevenue')}</h3>
          <p className="text-sm text-white/60">{t('coach.adjustParameters')}</p>
        </div>
      </div>

      {/* Plan Selector with Auto option */}
      <div className="flex gap-2 mb-4 p-1 bg-white/5 rounded-xl flex-wrap">
        <button
          onClick={() => setSelectedPlan('auto')}
          className={`flex-1 min-w-[80px] px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
            selectedPlan === 'auto'
              ? 'text-[#1a2d56]'
              : 'text-white/60 hover:text-white'
          }`}
          style={selectedPlan === 'auto' ? { background: COLORS.green } : {}}
        >
          <Sparkles className="w-3 h-3" />
          Auto
        </button>
        {pricingPlans.map((p) => (
          <button
            key={p.id}
            onClick={() => setSelectedPlan(p.id)}
            className={`flex-1 min-w-[80px] px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedPlan === p.id
                ? 'text-[#1a2d56]'
                : 'text-white/60 hover:text-white'
            }`}
            style={selectedPlan === p.id ? { background: COLORS.green } : {}}
          >
            {p.name}
          </button>
        ))}
      </div>

      {/* Auto-recommendation banner */}
      {selectedPlan === 'auto' && (
        <div className="mb-6 p-3 rounded-xl border-2 flex items-center gap-3"
          style={{ background: `${COLORS.green}10`, borderColor: COLORS.green }}>
          <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: COLORS.green }} />
          <div className="text-sm">
            <span className="text-white/70">{t('coach.recommendedPlan')}: </span>
            <span className="font-bold" style={{ color: COLORS.green }}>
              {plan.name} {hasAmbassadorBenefits ? 'Ambassador' : ''}
            </span>
            <span className="text-white/50 ml-2">({t('coach.bestForYourActivity')})</span>
          </div>
        </div>
      )}

      {/* Sliders */}
      <div className="space-y-6 mb-8">
        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm text-white/70">{t('coach.pricePerParticipant')}</label>
            <span className="font-bold text-white">{pricePerPerson}€</span>
          </div>
          <Slider
            value={[pricePerPerson]}
            onValueChange={([v]) => setPricePerPerson(v)}
            min={8}
            max={30}
            step={1}
            className="w-full"
          />
        </div>

        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm text-white/70">{t('coach.minThresholdLabel')}</label>
            <span className="font-bold text-white">{minParticipants}</span>
          </div>
          <Slider
            value={[minParticipants]}
            onValueChange={([v]) => setMinParticipants(v)}
            min={2}
            max={15}
            step={1}
            className="w-full"
          />
        </div>

        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm text-white/70">{t('coach.sessionsPerMonth')}</label>
            <span className="font-bold text-white">{sessionsPerMonth}</span>
          </div>
          <Slider
            value={[sessionsPerMonth]}
            onValueChange={([v]) => setSessionsPerMonth(v)}
            min={4}
            max={30}
            step={1}
            className="w-full"
          />
        </div>
      </div>

      {/* Results - Comparison Grid (or single column for Freemium) */}
      {hasAmbassadorBenefits ? (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Standard */}
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-white/50 text-xs mb-1">{plan.name} {t('coach.regularLabel')}</p>
              <p className="text-white/60 text-sm mb-1">{t('coach.monthlyNetRevenue')}</p>
              <p className="text-2xl font-bold text-white">{netRevenue}€</p>
            </div>
            {/* Ambassador */}
            <div className="rounded-xl p-4 text-center border-2 relative"
              style={{ background: `linear-gradient(135deg, ${COLORS.green}15, ${COLORS.green}05)`, borderColor: COLORS.green }}>
              <div className="absolute -top-2 right-2 px-2 py-0.5 text-xs font-bold rounded"
                style={{ background: COLORS.green, color: COLORS.darkBlue }}>
                {t('coach.ambassadorBadge')}
              </div>
              <p className="text-white/50 text-xs mb-1">{plan.name} Ambassador</p>
              <p className="text-white/60 text-sm mb-1">{t('coach.monthlyNetRevenue')}</p>
              <p className="text-3xl font-bold" style={{ color: COLORS.green }}>{ambassadorNetRevenue}€</p>
              <p className="text-xs mt-1" style={{ color: COLORS.green }}>+{monthlyBonus}€/{t('coach.month')}</p>
            </div>
          </div>

          {/* Yearly Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-xl p-3 text-center">
              <p className="text-white/60 text-xs mb-1">{t('coach.yearlyProjection')}</p>
              <p className="text-xl font-bold text-white">{yearlyRevenue.toLocaleString()}€</p>
            </div>
            <div className="rounded-xl p-3 text-center"
              style={{ background: `${COLORS.green}10` }}>
              <p className="text-white/60 text-xs mb-1">{t('coach.yearlyProjection')}</p>
              <p className="text-xl font-bold" style={{ color: COLORS.green }}>{ambassadorYearlyRevenue.toLocaleString()}€</p>
              <p className="text-xs" style={{ color: COLORS.green }}>+{(ambassadorYearlyRevenue - yearlyRevenue).toLocaleString()}€/{t('coach.year')}</p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Freemium - Single column, no Ambassador comparison */}
          <div className="bg-white/5 rounded-xl p-6 text-center mb-4">
            <p className="text-white/50 text-xs mb-1">Freemium</p>
            <p className="text-white/60 text-sm mb-1">{t('coach.monthlyNetRevenue')}</p>
            <p className="text-3xl font-bold text-white">{netRevenue}€</p>
          </div>

          {/* Yearly Projection */}
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <p className="text-white/60 text-xs mb-1">{t('coach.yearlyProjection')}</p>
            <p className="text-xl font-bold text-white">{yearlyRevenue.toLocaleString()}€</p>
          </div>
          
          {/* Note about no Ambassador for Freemium */}
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
            <p className="text-amber-400 text-sm">
              {t('coach.noAmbassadorFreemium')}
            </p>
          </div>
        </>
      )}

      {/* Details */}
      <div className="mt-4 p-3 bg-white/5 rounded-xl text-xs text-white/50">
        <div className="flex justify-between mb-1">
          <span>{t('coach.grossRevenue')} ({sessionsPerMonth} × {minParticipants} × {pricePerPerson}€)</span>
          <span className="text-white/70">{grossRevenue}€</span>
        </div>
        <div className="flex justify-between mb-1">
          <span>{t('coach.motivyaCommission')} ({hasAmbassadorBenefits ? plan.commission/2 : plan.commission}%)</span>
          <span className="text-red-400">-{Math.round(hasAmbassadorBenefits ? ambassadorCommissionAmount : commissionAmount)}€</span>
        </div>
        <div className="flex justify-between mb-1">
          <span>{t('coach.paymentFees')} (1,5%)</span>
          <span className="text-red-400">-{Math.round(paymentFeesAmount)}€</span>
        </div>
        {monthlyFee > 0 && (
          <div className="flex justify-between mb-1">
            <span>{t('coach.subscription')} {plan.name}</span>
            <span className="text-red-400">-{monthlyFee}€</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-white/10 text-white/70">
          <span className="font-medium">{t('coach.yourAdditionalRevenueTVAC')}</span>
          <span className="font-bold" style={{ color: COLORS.green }}>{hasAmbassadorBenefits ? ambassadorNetRevenue : netRevenue}€</span>
        </div>
      </div>

      <p className="text-center text-xs text-white/40 mt-4">
        * {t('coach.estimationNote')}
      </p>
    </div>
  );
}

// Ambassador Benefits Component
function AmbassadorBenefits({ t }) {
  const benefits = [
    { icon: Percent, title: t('coach.commissionDivided'), desc: t('coach.whileActive'), highlight: true },
    { icon: Crown, title: t('coach.founderBadge'), desc: t('coach.badgeForLife') },
    { icon: Rocket, title: t('coach.priorityVisibility'), desc: t('coach.topSearchResults') },
    { icon: Calendar, title: t('coach.earlyAccess'), desc: t('coach.newFeatures') },
    { icon: Users, title: t('coach.productInfluence'), desc: t('coach.participateDecisions') },
  ];

  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {benefits.map((benefit, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            className={`relative p-5 rounded-2xl border ${
              benefit.highlight 
                ? 'bg-gradient-to-br from-[#d7de3b]/20 to-[#d7de3b]/5 border-[#d7de3b]/30' 
                : 'bg-white/5 border-white/10'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
              benefit.highlight ? 'bg-[#d7de3b]/20' : 'bg-white/10'
            }`}>
              <benefit.icon className={`w-5 h-5 ${benefit.highlight ? 'text-[#d7de3b]' : 'text-white/70'}`} />
            </div>
            <h4 className={`font-bold mb-1 ${benefit.highlight ? 'text-[#d7de3b]' : 'text-white'}`}>
              {benefit.title}
            </h4>
            <p className="text-sm text-white/60">{benefit.desc}</p>
          </motion.div>
        ))}
      </div>
      
      {/* Ambassador Guarantee Messages */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-green-400" />
          </div>
          <h4 className="text-lg font-bold text-white">{t('coach.guaranteedReturn')}</h4>
        </div>
        
        <div className="space-y-3 text-sm text-white/70">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <p><span className="text-white font-medium">{t('coach.autoAdjustTitle')}</span> : {t('coach.autoAdjust')}</p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <p><span className="text-white font-medium">{t('coach.autoUpgradeTitle')}</span> : {t('coach.autoUpgrade')}</p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <p><span className="text-white font-medium">{t('coach.noPrePaymentTitle')}</span> : {t('coach.noPrePayment')}</p>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-xl font-bold" style={{ color: COLORS.green }}>{t('coach.readyGo')}</p>
        </div>
      </motion.div>
    </div>
  );
}

// Pricing Table Component
function PricingTable({ onSelectPlan, t }) {
  const pricingPlans = getPricingPlans(t);
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {pricingPlans.map((plan) => {
        const isPopular = plan.popular;
        
        return (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`relative rounded-2xl p-6 border-2 transition-all hover:scale-[1.02] ${
              isPopular 
                ? 'bg-gradient-to-br from-[#d7de3b]/10 to-transparent border-[#d7de3b]/50' 
                : 'bg-white/5 border-white/10'
            }`}
          >
            {isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 rounded-full text-xs font-bold" style={{ background: COLORS.green, color: COLORS.darkBlue }}>
                  {t('coach.popular')}
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-white">{plan.price}€</span>
                <span className="text-white/60">/{t('coach.month')}</span>
              </div>
              {plan.price > 0 && (
                <p className="text-xs text-white/40 mt-1">{t('coach.exVAT')}</p>
              )}
              {/* Commission Motivya - separate line */}
              <div className={`mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                plan.color === 'yellow' ? 'bg-[#d7de3b]/20 text-[#d7de3b]' : 
                plan.color === 'primary' ? 'bg-cyan-500/20 text-cyan-400' : 
                'bg-white/10 text-white/70'
              }`}>
                <Percent className="w-3 h-3" />
                {plan.commission}% {t('coach.commissionMotivya')}
              </div>
              {/* Transaction fees - separate line */}
              <p className="text-xs text-white/50 mt-1">
                + 1,5% {t('coach.transactionFees')}
              </p>
              <p className="text-xs text-white/30 mt-0.5">
                {t('coach.transactionFeesNote')}
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  {feature.included ? (
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-white/30 flex-shrink-0" />
                  )}
                  <span className={feature.included ? 'text-white/90' : 'text-white/40'}>
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => onSelectPlan && onSelectPlan(plan.id)}
              className={`w-full py-3 ${
                isPopular 
                  ? 'text-[#1a2d56] font-bold' 
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
              style={isPopular ? { background: COLORS.green } : {}}
            >
              {plan.cta}
            </Button>
          </motion.div>
        );
      })}
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export default function BecomeCoachPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useTranslation();
  
  const [step, setStep] = useState(0);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [launchStats, setLaunchStats] = useState(DEFAULT_STATS);
  const heroRef = useRef(null);
  const formRef = useRef(null);

  // Scroll to top on page load OR to form if #form hash is present
  useEffect(() => {
    if (location.hash === '#form') {
      // If #form hash is present, show the form directly
      setStep(1);
    } else if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  // Fetch dynamic ambassador count
  useEffect(() => {
    const fetchAmbassadorCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ambassador-count`);
        if (response.ok) {
          const data = await response.json();
          // API already returns displayed count with +11 offset
          setLaunchStats({
            totalSpots: data.total || 90,
            currentCoaches: data.current || 11,
            closingDate: DEFAULT_STATS.closingDate,
            spotsRemaining: data.spots_remaining,
            isFull: data.is_full
          });
        }
      } catch (error) {
        console.log('Using default stats');
      }
    };
    fetchAmbassadorCount();
  }, []);

  // Scroll detection for sticky CTA
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowStickyCTA(heroBottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    // Go directly to the form (step 1)
    setStep(1);
  };

  // If user is already a coach
  if (user?.is_coach || user?.role === 'coach') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: COLORS.darkBlue }}>
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: COLORS.green }} />
          <h2 className="text-2xl font-bold text-white mb-2">{t('coach.alreadyCoachTitle')}</h2>
          <p className="text-white/60 mb-6">{t('coach.accessDashboard')}</p>
          <Button 
            onClick={() => navigate('/coach/dashboard')} 
            className="w-full py-3"
            style={{ background: COLORS.green, color: COLORS.darkBlue }}
          >
            {t('coach.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  // ==================== LANDING PAGE ====================
  if (step === 0) {
    return (
      <div className="min-h-screen" style={{ background: COLORS.darkBlue }}>
        {/* Header */}
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#1a2d56]/80 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <Link to="/" className="font-bold text-xl text-white tracking-tight">MOTIVYA</Link>
              <LanguageSelector />
            </div>
          </div>
        </header>

        {/* SECTION 1: HERO */}
        <section ref={heroRef} className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#d7de3b]/5 via-transparent to-transparent" />
          <div className="max-w-5xl mx-auto px-4 relative">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              {/* Launch Badge */}
              <motion.div 
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-6"
                style={{ background: `${COLORS.green}15`, borderColor: `${COLORS.green}30`, color: COLORS.green }}
              >
                <Sparkles className="w-4 h-4" />
                {t('coach.launchBadge')}
              </motion.div>

              {/* Main Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('coach.heroMainTitle')}{' '}
                <span style={{ color: COLORS.green }}>{t('coach.heroFoundingCoaches')}</span>
                <br />{t('coach.heroInBrussels')}
              </h1>

              {/* Subtitle */}
              <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto mb-8">
                {t('coach.heroSubtitle')} <span className="text-white font-medium">{t('coach.heroSubtitleBold')}</span>
              </p>

              {/* Counter */}
              <div className="mb-10">
                <LaunchCounter current={launchStats.currentCoaches} total={launchStats.totalSpots} t={t} />
              </div>

              {/* CTA */}
              <Button 
                onClick={scrollToForm}
                className="text-lg px-10 py-6 font-bold rounded-xl shadow-2xl hover:scale-105 transition-transform"
                style={{ background: COLORS.green, color: COLORS.darkBlue }}
                data-testid="hero-cta"
              >
                <Rocket className="w-5 h-5 mr-2" />
                {t('coach.becomeAmbassadorCoach')}
              </Button>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: OPPORTUNITY */}
        <section className="py-16 bg-white/[0.02]">
          <div className="max-w-5xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('coach.opportunitySectionTitle')} <span style={{ color: COLORS.green }}>{t('coach.opportunityFirstCoaches')}</span>
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto">
                {t('coach.opportunityDesc')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Target, title: t('coach.maxVisibility'), desc: t('coach.maxVisibilityDesc') },
                { icon: Users, title: t('coach.priorityAccess'), desc: t('coach.priorityAccessDesc') },
                { icon: MapPin, title: t('coach.localDomination'), desc: t('coach.localDominationDesc') },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center"
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${COLORS.green}20` }}>
                    <item.icon className="w-7 h-7" style={{ color: COLORS.green }} />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3: NO RISK MODEL */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-4"
                style={{ background: `${COLORS.green}15`, borderColor: `${COLORS.green}30`, color: COLORS.green }}>
                <Shield className="w-4 h-4" />
                {t('coach.uniqueModel')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('coach.noLossTitle')} <span style={{ color: COLORS.green }}>{t('coach.noLossHighlight')}</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Explanation */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-5"
              >
                {[
                  { icon: Target, color: COLORS.green, title: t('coach.defineThreshold'), desc: t('coach.defineThresholdDesc') },
                  { icon: CheckCircle, color: '#22c55e', title: t('coach.thresholdReached'), desc: t('coach.thresholdReachedDesc') },
                  { icon: XCircle, color: '#ef4444', title: t('coach.thresholdNotReached'), desc: t('coach.thresholdNotReachedDesc') },
                  { icon: PiggyBank, color: '#eab308', title: t('coach.zeroLoss'), desc: t('coach.zeroLossDesc') },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}20` }}>
                      <item.icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-white/60 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Concrete Example - Ambassador vs Standard Comparison */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl p-6 border"
                style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(34,197,94,0.02))', borderColor: 'rgba(34,197,94,0.2)' }}
              >
                <h4 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-green-400" />
                  {t('coach.exampleTitle')}
                </h4>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-white/60">{t('coach.pricePerPart')}</span>
                    <span className="font-bold text-white">12€</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-white/60">{t('coach.minThreshold')}</span>
                    <span className="font-bold" style={{ color: COLORS.green }}>5 {t('coach.participants')}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-white/60">{t('coach.grossRev')}</span>
                    <span className="font-bold text-white">60€</span>
                  </div>
                </div>

                {/* Comparison Grid: Standard vs Ambassador */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* Standard Coach */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-white/50 text-xs mb-2 text-center">{t('coach.regularCoach')}</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-white/50">{t('coach.commission')} (20%)</span>
                        <span className="text-red-400">-12€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">{t('coach.transactionFees')} (1,5%)</span>
                        <span className="text-red-400">-0,90€</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-white/10">
                        <span className="text-white/70 font-medium">{t('coach.yourNetIncome')}</span>
                        <span className="font-bold text-white">47,10€</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ambassador Coach */}
                  <div className="rounded-xl p-4 border-2 relative"
                    style={{ background: `linear-gradient(135deg, ${COLORS.green}15, ${COLORS.green}05)`, borderColor: COLORS.green }}>
                    <div className="absolute -top-2 right-2 px-2 py-0.5 text-xs font-bold rounded"
                      style={{ background: COLORS.green, color: COLORS.darkBlue }}>
                      {t('coach.ambassadorBadge')}
                    </div>
                    <p className="text-white/50 text-xs mb-2 text-center">{t('coach.ambassadorCoach')}</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-white/50">{t('coach.commission')} (10%)</span>
                        <span className="text-red-400">-6€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">{t('coach.transactionFees')} (1,5%)</span>
                        <span className="text-red-400">-0,90€</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-white/10">
                        <span className="font-medium" style={{ color: COLORS.green }}>{t('coach.yourNetIncome')}</span>
                        <span className="font-bold text-lg" style={{ color: COLORS.green }}>53,10€</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ambassador Advantage Highlight */}
                <div className="p-4 rounded-xl text-center"
                  style={{ background: `${COLORS.green}15`, border: `1px solid ${COLORS.green}30` }}>
                  <p className="font-bold mb-1" style={{ color: COLORS.green }}>
                    +6€ {t('coach.perSessionBonus')}
                  </p>
                  <p className="text-white/60 text-sm">{t('coach.ambassadorAdvantageDesc')}</p>
                </div>

                {/* Monthly projection */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white/5 rounded-xl text-center">
                    <p className="text-white/50 text-xs mb-1">{t('coach.regularCoach')} (12 {t('coach.sessions')})</p>
                    <p className="text-xl font-bold text-white">565€</p>
                  </div>
                  <div className="p-3 rounded-xl text-center"
                    style={{ background: `${COLORS.green}15` }}>
                    <p className="text-white/50 text-xs mb-1">{t('coach.ambassadorCoach')} (12 {t('coach.sessions')})</p>
                    <p className="text-2xl font-bold" style={{ color: COLORS.green }}>637€</p>
                    <p className="text-xs" style={{ color: COLORS.green }}>+72€/{t('coach.month')}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION - JOURNEY SIMULATOR */}
        <section className="py-16 bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="max-w-4xl mx-auto px-4">
            <JourneySimulator />
          </div>
        </section>

        {/* SECTION 4: SIMULATOR */}
        <section className="py-16 bg-white/[0.02]">
          <div className="max-w-3xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <RevenueSimulator t={t} />
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: AMBASSADOR STATUS */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-4"
                style={{ background: `${COLORS.green}15`, borderColor: `${COLORS.green}30`, color: COLORS.green }}>
                <Crown className="w-4 h-4" />
                {t('coach.exclusiveLaunch')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('coach.ambassadorStatus')} <span style={{ color: COLORS.green }}>{t('coach.coachAmbassadorTitle')}</span>
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto">
                {t('coach.noRiskAdvantage')}
              </p>
            </motion.div>

            <AmbassadorBenefits t={t} />
          </div>
        </section>

        {/* SECTION 6: HOW IT WORKS */}
        <section className="py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('coach.howItWorksTitle')}</h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: 1, icon: Users, title: t('coach.step1Title'), desc: t('coach.step1Desc') },
                { step: 2, icon: Calendar, title: t('coach.step2Title'), desc: t('coach.step2Desc') },
                { step: 3, icon: Euro, title: t('coach.step3Title'), desc: t('coach.step3Desc') },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative text-center"
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ background: COLORS.green, color: COLORS.darkBlue }}>
                    {item.step}
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 pt-10">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 bg-white/10">
                      <item.icon className="w-6 h-6 text-white/70" />
                    </div>
                    <h3 className="font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7: PRICING */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('coach.choosePlanTitle')}</h2>
              <p className="text-white/60">
                <span style={{ color: COLORS.green }} className="font-medium">{t('coach.ambassadorAdvantages')}</span> {t('coach.exclusiveAdvantages')}
              </p>
            </motion.div>

            <PricingTable onSelectPlan={() => scrollToForm()} t={t} />
          </div>
        </section>

        {/* SECTION 8: URGENCY */}
        <section className="py-16 bg-gradient-to-b from-transparent via-red-500/5 to-transparent">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-8 md:p-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 text-red-400 text-sm font-medium mb-6">
                <Timer className="w-4 h-4" />
                {t('coach.limitedOfferBadge')}
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('coach.ambassadorSpotsTitle')}
              </h2>
              
              <p className="text-white/70 text-lg mb-6">
                <span className="text-white font-bold">{t('coach.whileActive')}</span>, {t('coach.commissionHalvedBenefit')}.<br />
                {t('coach.freemiumUnchanged')} <span className="text-white/50">{t('coach.paymentFeesNote')}</span>
              </p>

              {/* Ambassador offer details */}
              <div className="grid grid-cols-2 gap-4 mb-8 max-w-md mx-auto">
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-2xl font-bold" style={{ color: COLORS.green }}>10%</p>
                  <p className="text-xs text-white/60">{t('coach.insteadOfPercent')} 20%</p>
                  <p className="text-sm text-white/80 mt-1">Active</p>
                  <p className="text-xs text-white/40">{t('coach.paymentFeesNote')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-2xl font-bold" style={{ color: COLORS.green }}>5%</p>
                  <p className="text-xs text-white/60">{t('coach.insteadOfPercent')} 10%</p>
                  <p className="text-sm text-white/80 mt-1">Premium</p>
                  <p className="text-xs text-white/40">{t('coach.paymentFeesNote')}</p>
                </div>
              </div>

              <div className="mb-8">
                <LaunchCounter current={launchStats.currentCoaches} total={launchStats.totalSpots} t={t} />
              </div>

              <Button 
                onClick={scrollToForm}
                className="text-lg px-10 py-6 font-bold rounded-xl"
                style={{ background: COLORS.green, color: COLORS.darkBlue }}
              >
                {t('coach.reserveAmbassadorSpot')}
              </Button>
            </motion.div>
          </div>
        </section>

        {/* SECTION 9: FINAL CTA */}
        <section ref={formRef} className="py-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('coach.getAhead')}<br />
                <span style={{ color: COLORS.green }}>{t('coach.rightNow')}</span>
              </h2>
              
              <p className="text-white/60 text-lg mb-8">
                {t('coach.joinTheCoaches').replace('{count}', launchStats.currentCoaches)}
              </p>

              <Button 
                onClick={() => setStep(1)}
                className="text-xl px-12 py-7 font-bold rounded-xl shadow-2xl hover:scale-105 transition-transform"
                style={{ background: COLORS.green, color: COLORS.darkBlue }}
                data-testid="final-cta"
              >
                <Rocket className="w-6 h-6 mr-3" />
                {t('coach.becomeAmbassadorCoach')}
              </Button>

              <p className="mt-6 text-white/40 text-sm">
                {t('coach.freeSignup')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Sticky CTA */}
        <StickyCTA onClick={() => setStep(1)} visible={showStickyCTA} t={t} />

        {/* Footer */}
        <footer className="py-8 border-t border-white/10">
          <div className="max-w-5xl mx-auto px-4 text-center text-white/40 text-sm">
            © 2026 Motivya. {t('coach.allRightsReserved')}
          </div>
        </footer>
      </div>
    );
  }

  // ==================== APPLICATION FORM (Step 1) ====================
  return (
    <div className="min-h-screen" style={{ background: COLORS.darkBlue }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#1a2d56]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(0)} className="text-white/60 hover:text-white">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <Link to="/" className="font-bold text-xl text-white tracking-tight">MOTIVYA</Link>
            <div className="w-6" />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-4"
            style={{ background: `${COLORS.green}15`, borderColor: `${COLORS.green}30`, color: COLORS.green }}>
            <Crown className="w-4 h-4" />
            {t('coach.ambassadorProgram')}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('coach.createYourProfile')}</h1>
          <p className="text-white/60">{t('coach.remainingSpotsJoin').replace('{remaining}', launchStats.totalSpots - launchStats.currentCoaches)}</p>
        </motion.div>

        {/* Simplified Form */}
        <SimpleCoachForm 
          onSuccess={() => {
            toast.success(t('coach.applicationSent'));
            navigate('/');
          }}
          t={t}
        />
      </div>
    </div>
  );
}
